import React, { useState } from 'react';
import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Text,
  Title,
  Progress,
  Stack,
  SimpleGrid,
  Paper,
  MultiSelect,
  Select,
  NumberInput,
  Flex,
  Tooltip,
  useMantineTheme,
  Card
} from '@mantine/core';
import { IconHome, IconExternalLink } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useMobileDetection } from '../utils/deviceUtils';

const VENDOR_COLOR = { aws: '#e8590c', microsoft: '#1c7ed6', google: '#34A853' };
const VENDOR_LABEL = { aws: 'AWS', microsoft: 'Microsoft', google: 'Google' };
const STATUS_META = {
  enrolled:  { label: 'Enrolled',  color: 'violet' },
  learning:  { label: 'Learning',  color: 'blue'   },
  certified: { label: 'Certified', color: 'green'  },
  retired:   { label: 'Retired',   color: 'gray'   },
};

// 資格IDを表示名に変換する関数
const getCertificationDisplayName = (certificationId) => {
  const certificationMap = {
    'az400': 'AZ-400',
    'az500': 'AZ-500',
    'ai102': 'AI-102',
    'md102': 'MD-102',
    'az104': 'AZ-104',
    'az305': 'AZ-305',
    'az800': 'AZ-800',
    'az700': 'AZ-700',
    'saac03': 'SAA-C03',
    'sapc02': 'SAP-C02',
    'ansc01': 'ANS-C01',
    'soac02': 'SOA-C02',
    'scsc02': 'SCS-C02',
    'dvac02': 'DVA-C02',
    'dopc02': 'DOP-C02',
    'deac01': 'DEA-C01',
    'mlac01': 'MLA-C01',
    'mlsc01': 'MLS-C01',
    'clfc02': 'CLF-C02',
    'aifc01': 'AIF-C01',
    'aipc01': 'AIP-C01',
    'soac03': 'SOA-C03',
  };
  return certificationMap[certificationId] || certificationId;
};

// 進捗表示に利用するステータス定義
const buildStatusColors = (theme, colorName) => {
  const palette = theme.colors[colorName];
  const isDark = theme.colorScheme === 'dark';

  return {
    // ライト/ダークでコントラストを確保するため、明度の異なるインデックスを使い分ける
    backgroundColor: isDark ? palette[9] : palette[0],
    borderColor: isDark ? palette[6] : palette[3],
    labelColor: isDark ? palette[1] : palette[7],
    valueColor: isDark ? palette[0] : palette[9],
  };
};

// 未回答のみ背景と文字色をよりコントラストの高い色に調整する
// ダークモード時に数値が背景へ沈むのを防ぐための専用設定
const buildUnansweredColors = (theme) => {
  const palette = theme.colors.gray;
  const isDark = theme.colorScheme === 'dark';

  return {
    backgroundColor: isDark ? palette[7] : palette[0],
    borderColor: isDark ? palette[5] : palette[3],
    labelColor: isDark ? theme.white : palette[7],
    valueColor: isDark ? theme.white : palette[8],
  };
};

// ステータスごとの見た目をテーマから生成するファクトリ
const getStatusItems = (theme) => [
  {
    key: 'clear',
    label: '正解',
    progressKey: 'clearProgress',
    progressColor: 'teal',
    getStyles: () => buildStatusColors(theme, 'teal'),
  },
  {
    key: 'error',
    label: '不正解',
    progressKey: 'errorProgress',
    progressColor: 'red',
    getStyles: () => buildStatusColors(theme, 'red'),
  },
  {
    key: 'excluded',
    label: '除外',
    progressKey: 'excludedProgress',
    progressColor: 'yellow',
    getStyles: () => buildStatusColors(theme, 'yellow'),
  },
  {
    key: 'unanswered',
    label: '未回答',
    progressKey: 'unansweredProgress',
    progressColor: 'gray',
    getStyles: () => buildUnansweredColors(theme, 'gray'),
  },
];

// ステータスごとのバッジ表示をまとめたコンポーネント
const StatusPills = ({ data, justify = 'flex-start', statusItemsOverride }) => {
  const theme = useMantineTheme();
  const statusItems = statusItemsOverride || getStatusItems(theme);

  return (
    <Group gap={6} wrap="wrap" justify={justify} align="flex-start">
      {statusItems.map((status) => {
        const styles = status.getStyles(theme);
        const isUnanswered = status.key === 'unanswered'; // 未回答のみ色を薄くするためのフラグ

        return (
          <Box
            key={status.key}
            px="sm"
            py={4}
            sx={() => ({
              backgroundColor: styles.backgroundColor,
              border: `1px solid ${styles.borderColor}`,
              borderRadius: theme.radius.md,
              minWidth: '104px',
            })}
          >
            <Text fz={11} fw={600} c={isUnanswered ? 'var(--mantine-color-text)' : styles.labelColor}>
              {status.label}
            </Text>
            <Text fz="sm" fw={700} c={isUnanswered ? 'var(--mantine-color-text)' : styles.valueColor}>
              {(data?.[status.key] ?? 0)}問
            </Text>
          </Box>
        );
      })}
    </Group>
  );
};

// プログレスバーのセクションをまとめて描画するためのユーティリティ
const renderProgressSections = (data, statusItems) => (
  statusItems.map((status) => (
    <Progress.Section
      key={status.key}
      value={data?.[status.progressKey] ?? 0}
      color={status.progressColor}
    />
  ))
);

const returnTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

// カテゴリ・タスクごとのカード表示コンポーネント
const ProgressCard = ({ label, data, onClick }) => {
  const theme = useMantineTheme();
  const statusItems = getStatusItems(theme);

  if (!data) {
    return null;
  }

  return (
    <Card
      p="md"
      style={{
        width: '100%',
        cursor: 'pointer',
      }}
      // カードクリックでフィルタへ反映できるようクリックハンドラを受け取る
      onClick={() => {
        onClick();
        returnTop();
      }}
    >
      <Stack gap="sm">
        <Text fw={600} fz="md">
          {label}
        </Text>
        <Text fz={12} c='var(--mantine-color-text)'>
          {data.totalQuestions}問
        </Text>
        <Progress.Root>
          {renderProgressSections(data, statusItems)}
        </Progress.Root>
        <StatusPills data={data} statusItemsOverride={statusItems} />
      </Stack>
    </Card>
  );
};

function FilterView({
  questions,
  results,
  selectedTopic,
  setSelectedTopic,
  categories = [],
  selectedCategory = [],
  setSelectedCategory,
  tasks = [],
  selectedTask = [],
  setSelectedTask = () => {},
  selectedResult,
  setSelectedResult,
  questionOrder,
  setQuestionOrder,
  selectedQuestions = [],
  setSelectedQuestions = () => {},
  selectedCertification, // 資格選択を受け取る
  setSelectedCertification, // 資格選択の変更関数を受け取る
  progressData, // 進捗データを受け取る
  onStart,
  lastQuestionId,
  resumeFilterState,
  onResume,
  examGuideUrl,
  certFormalName,
  certVendor,
  certUserStatus,
}) {
  const [startPosition, setStartPosition] = useState(1);
  const theme = useMantineTheme();
  const statusItems = getStatusItems(theme);
  const navigate = useNavigate();
  const isMobile = useMobileDetection();
  const topics = ['すべて', ...Array.from(new Set(questions.map(q => q.topic?.toString()))).sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }))];
  // 「除外」を含む集計結果へ合わせて結果フィルターも更新
  const resultsList = ['すべて', '正解', '不正解', '除外', '未回答'];
  const categoryList = ['すべて', ...Array.from(new Set((categories || []).filter(value => value && value !== 'すべて'))).sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }))];
  const taskList = ['すべて', ...Array.from(new Set((tasks || []).filter(value => value && value !== 'すべて'))).sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }))];
  // 質問番号は正規化テーブルの number カラムを参照する
  const questionNumbers = Array.from(new Set((questions || []).map((question) => question?.number?.toString()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'ja', { numeric: true }));

  // 旧仕様での単一選択への対応も考慮し、配列形式に正規化するヘルパー関数
  const normalizeSelection = (value) => {
    if (Array.isArray(value)) {
      return value;
    }
    if (value === 'すべて' || value === undefined || value === null) {
      return [];
    }
    return [value];
  };

  // ドロップダウンに渡す値を正規化
  const normalizedTopics = normalizeSelection(selectedTopic);
  const normalizedResults = normalizeSelection(selectedResult);
  const normalizedCategories = normalizeSelection(selectedCategory);
  const normalizedTasks = normalizeSelection(selectedTask);
  const normalizedQuestions = normalizeSelection(selectedQuestions);

  // 変更時には空配列を「すべて」として扱う
  const handleTopicChange = (value) => {
    setSelectedTopic(value.length === 0 ? [] : value);
  };

  const handleResultChange = (value) => {
    setSelectedResult(value.length === 0 ? [] : value);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value.length === 0 ? [] : value);
  };

  const handleTaskChange = (value) => {
    setSelectedTask(value.length === 0 ? [] : value);
  };

  // カテゴリカードのクリックでカテゴリフィルタへ追加する
  const handleCategoryCardClick = (categoryName) => {
    if (!categoryName) {
      return;
    }
    const nextCategories = normalizedCategories.includes(categoryName)
      ? normalizedCategories
      : [...normalizedCategories, categoryName];
    setSelectedCategory(nextCategories.length === 0 ? [] : nextCategories);
  };

  // タスクカードのクリックでタスクフィルタへ追加する
  const handleTaskCardClick = (taskName) => {
    if (!taskName) {
      return;
    }
    const nextTasks = normalizedTasks.includes(taskName)
      ? normalizedTasks
      : [...normalizedTasks, taskName];
    setSelectedTask(nextTasks.length === 0 ? [] : nextTasks);
  };

  // 出題範囲（設問番号）の選択変更を処理
  const handleQuestionChange = (value) => {
    setSelectedQuestions(value.length === 0 ? [] : value);
  };

  return (
    <Box
      style={{
        backgroundColor: 'light-dark(#f5f4ed, #141413)',
        minHeight: '100vh',
        paddingTop: isMobile ? '1.5rem' : '3rem',
        paddingBottom: isMobile ? '1.5rem' : '3rem',
      }}
    >
      <Container size="xl" px={isMobile ? 'md' : 'lg'}>
        <Paper shadow="none" radius="lg" p={isMobile ? 'xl' : '2xl'} sx={{ border: 'none' }} style={{ background: 'transparent' }}>
          <Stack gap={isMobile ? 'xl' : '2xl'}>
            {/* モバイル専用のホームボタン */}
            {isMobile && (
              <Group justify="flex-start">
                <Button
                  variant="outline"
                  onClick={() => navigate('/Study')}
                  size="sm"
                  px="xs"
                >
                  <IconHome size={16} />
                </Button>
              </Group>
            )}

            {/* サマリー表示 */}
            {progressData && (
              <Stack gap={isMobile ? 'lg' : 'xl'}>
                <Stack gap="sm" align="center">
                  {/* ベンダー・ステータスバッジ */}
                  {(certVendor || certUserStatus) && (
                    <Group justify="center" gap="xs">
                      {certVendor && (() => {
                        const vk = certVendor.toLowerCase();
                        return (
                          <Badge color={VENDOR_COLOR[vk] ?? 'gray'} variant="filled" style={{ textTransform: 'none' }}>
                            {VENDOR_LABEL[vk] ?? certVendor}
                          </Badge>
                        );
                      })()}
                      {certUserStatus && STATUS_META[certUserStatus] && (
                        <Badge color={STATUS_META[certUserStatus].color} variant="filled" style={{ textTransform: 'none' }}>
                          {STATUS_META[certUserStatus].label}
                        </Badge>
                      )}
                    </Group>
                  )}
                  {certFormalName && (
                    <Title order={2} ta="center">
                      {certFormalName}
                    </Title>
                  )}
                  {selectedCertification && (
                    <Text size="sm" c="dimmed" ta="center">
                      {getCertificationDisplayName(selectedCertification)}
                    </Text>
                  )}
                  {examGuideUrl && (
                    <Anchor href={examGuideUrl} target="_blank" rel="noopener noreferrer" size="sm">
                      <Group gap={4} justify="center">
                        <IconExternalLink size={14} />
                        試験ガイド
                      </Group>
                    </Anchor>
                  )}
                </Stack>
                <StatusPills data={progressData} justify="center" />
                <Progress.Root>
                  {renderProgressSections(progressData, statusItems)}
                </Progress.Root>
                <Text
                  ta="center"
                  size="sm"
                  // テーマの本文色に寄せて、どちらのカラースキームでも読みやすい文字色にする
                  c="var(--mantine-color-text)"
                >
                  総問題数: {progressData.totalQuestions}問
                </Text>

                {/* フィルター選択肢 */}
                <Flex gap="xs">
                  {/* Topic 選択 */}
                  <Box>
                    <MultiSelect
                      label="Topic"
                      placeholder="すべて"
                      data={topics.filter(topic => topic !== 'すべて').map(topic => ({ value: topic, label: topic }))}
                      value={normalizedTopics}
                      onChange={handleTopicChange}
                      searchable
                      nothingFoundMessage="一致するTopicがありません"
                      comboboxProps={{ withinPortal: false }}
                      clearable
                      hidePickedOptions
                      size="sm"
                      data-testid="filter-topic"
                    />
                  </Box>

                  {/* 出題範囲 */}
                  <Box>
                    <MultiSelect
                      label="Question"
                      placeholder="すべて"
                      data={questionNumbers.map((questionNumber) => ({ value: questionNumber, label: questionNumber }))}
                      value={normalizedQuestions}
                      onChange={handleQuestionChange}
                      searchable
                      nothingFoundMessage="一致する問題番号がありません"
                      comboboxProps={{ withinPortal: false }}
                      clearable
                      hidePickedOptions
                      size="sm"
                      data-testid="filter-question"
                    />
                  </Box>
                        
                  {/* カテゴリ選択 */}
                  <Box>
                    <MultiSelect
                      label="カテゴリ"
                      placeholder="すべて"
                      data={categoryList.filter(category => category !== 'すべて').map(category => ({ value: category, label: category }))}
                      value={normalizedCategories}
                      onChange={handleCategoryChange}
                      searchable
                      nothingFoundMessage="一致するカテゴリがありません"
                      comboboxProps={{ withinPortal: false }}
                      clearable
                      hidePickedOptions
                      size="sm"
                      data-testid="filter-category"
                    />
                  </Box>
                        
                  {/* タスク選択 */}
                  <Box>
                    <MultiSelect
                      label="タスク"
                      placeholder="すべて"
                      data={taskList.filter(task => task !== 'すべて').map(task => ({ value: task, label: task }))}
                      value={normalizedTasks}
                      onChange={handleTaskChange}
                      searchable
                      nothingFoundMessage="一致するタスクがありません"
                      comboboxProps={{ withinPortal: false }}
                      clearable
                      hidePickedOptions
                      size="sm"
                      data-testid="filter-task"
                    />
                  </Box>
                        
                  {/* ステータス選択 */}
                  <Box>
                    <MultiSelect
                      label="ステータス"
                      placeholder="すべて"
                      data={resultsList.filter(result => result !== 'すべて').map(result => ({ value: result, label: result }))}
                      value={normalizedResults}
                      onChange={handleResultChange}
                      searchable
                      nothingFoundMessage="一致する結果がありません"
                      comboboxProps={{ withinPortal: false }}
                      clearable
                      hidePickedOptions
                      size="sm"
                      data-testid="filter-result"
                    />
                  </Box>
                        
                  {/* 出題順序 */}
                  <Box>
                    <Select
                      label="順番"
                      data={[
                        { value: 'first', label: '最初から' },
                        { value: 'last', label: '最後から' },
                      ]}
                      value={questionOrder}
                      onChange={(value) => value && setQuestionOrder(value)}
                      placeholder="選択してください"
                      comboboxProps={{ withinPortal: false }}
                      size="sm"
                      data-testid="filter-order"
                    />
                  </Box>
                </Flex>
                <Container>
                  <Group gap="md" justify="center" align="flex-end">
                    <NumberInput
                      label="開始位置"
                      description={`1〜${questions.length}`}
                      value={startPosition}
                      onChange={(val) => setStartPosition(val || 1)}
                      min={1}
                      max={questions.length || 1}
                      clampBehavior="strict"
                      size="sm"
                      style={{ width: 100 }}
                    />
                    <Button
                      variant="filled"
                      radius="sm"
                      size="xl"
                      onClick={() => onStart(startPosition)}
                    >
                      START
                    </Button>
                    {lastQuestionId && onResume && (() => {
                      const q = questions.find(q => String(q.id) === String(lastQuestionId));
                      const fs = resumeFilterState ?? {};
                      const fmt = (arr) => Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : 'なし';
                      const orderLabel = fs.questionOrder === 'last' ? '最後から' : '最初から';
                      const tooltipLabel = (
                        <Stack gap={4} p={4}>
                          <Text size="xs" fw={700}>■ 問題位置</Text>
                          <Text size="xs">
                            {q ? `Topic ${q.topic ?? '-'} - Question ${q.number ?? '-'}` : `ID: ${lastQuestionId}`}
                          </Text>
                          <Text size="xs" fw={700} mt={4}>■ フィルター設定</Text>
                          <Text size="xs">Topic: {fmt(fs.selectedTopic)}</Text>
                          <Text size="xs">カテゴリ: {fmt(fs.selectedCategory)}</Text>
                          <Text size="xs">タスク: {fmt(fs.selectedTask)}</Text>
                          <Text size="xs">ステータス: {fmt(fs.selectedResult)}</Text>
                          <Text size="xs">順番: {orderLabel}</Text>
                        </Stack>
                      );
                      return (
                        <Tooltip label={tooltipLabel} withArrow multiline position="top">
                          <Button
                            variant="outline"
                            radius="sm"
                            size="xl"
                            onClick={onResume}
                          >
                            前回の続きから
                          </Button>
                        </Tooltip>
                      );
                    })()}
                  </Group>
                </Container>

                {progressData.categoryBreakdown && progressData.categoryBreakdown.length > 0 && (
                  <Stack gap="sm">
                    <Box>
                      <Title order={4}>カテゴリ</Title>
                    </Box>
                    {/* グリッド幅を圧縮して3列レイアウトにする */}
                    <SimpleGrid
                      cols={3}
                      spacing="md"
                      breakpoints={[
                        { maxWidth: 'lg', cols: 2 },
                        { maxWidth: 'sm', cols: 1 },
                      ]}
                    >
                      {progressData.categoryBreakdown.map((item) => (
                        <ProgressCard
                          key={item.name}
                          label={item.name}
                          data={item}
                          onClick={() => handleCategoryCardClick(item.name)}
                        />
                      ))}
                    </SimpleGrid>
                  </Stack>
                )}

                {progressData.taskBreakdown && progressData.taskBreakdown.length > 0 && (
                  <Stack gap="sm">
                    <Box>
                      <Title order={4}>タスク</Title>
                    </Box>
                    {/* タスクも同様に3列レイアウトへ変更 */}
                    <SimpleGrid
                      cols={3}
                      spacing="md"
                      breakpoints={[
                        { maxWidth: 'lg', cols: 2 },
                        { maxWidth: 'sm', cols: 1 },
                      ]}
                    >
                      {progressData.taskBreakdown.map((item) => (
                        <ProgressCard
                          key={item.name}
                          label={item.name}
                          data={item}
                          onClick={() => handleTaskCardClick(item.name)}
                        />
                      ))}
                    </SimpleGrid>
                  </Stack>
                )}
              </Stack>
            )}

          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default FilterView;
